// Docs: IMPLEMENTATION.md §3.4 (Debug), FEATURES.md §13 (Preferences, background jobs, debug)
import React from 'react';

const GITHUB_REPO = 'https://github.com/permin/german_trainer';
const NETLIFY_PROJECT = 'https://app.netlify.com/projects/relaxed-druid-9eb342';
const POSTHOG_PROJECT = 'https://eu.posthog.com/project/258602';
const SENTRY_ORG = 'https://home-dgn.sentry.io';

const apiOrigin = () => {
  const raw = import.meta.env.VITE_API_URL || 'http://localhost:8001/api/v1';
  try {
    return new URL(raw, window.location.origin).origin;
  } catch {
    return raw;
  }
};

// Every target sits behind its own login — these are deep links, not a way in.
const LINK_GROUPS = [
  {
    title: 'Hosting & deploys',
    links: [
      { label: 'Railway — dashboard', href: 'https://railway.com/dashboard', note: 'backend, worker, Postgres, Redis' },
      { label: 'Netlify — project', href: NETLIFY_PROJECT, note: 'frontend SPA' },
      { label: 'Netlify — deploys', href: `${NETLIFY_PROJECT}/deploys` },
      { label: 'Netlify — env vars', href: `${NETLIFY_PROJECT}/configuration/env` },
    ],
  },
  {
    title: 'Monitoring',
    links: [
      { label: 'Sentry — all issues', href: `${SENTRY_ORG}/issues/`, note: 'org home-dgn' },
      { label: 'Sentry — backend', href: `${SENTRY_ORG}/projects/python-fastapi/`, note: 'python-fastapi' },
      { label: 'Sentry — frontend', href: `${SENTRY_ORG}/projects/javascript-react/`, note: 'javascript-react' },
      { label: 'PostHog — project', href: POSTHOG_PROJECT, note: 'product analytics' },
      { label: 'PostHog — activity', href: `${POSTHOG_PROJECT}/activity/explore` },
    ],
  },
  {
    title: 'AI providers',
    links: [
      { label: 'OpenAI — platform', href: 'https://platform.openai.com/', note: 'the "API hub"' },
      { label: 'OpenAI — usage & cost', href: 'https://platform.openai.com/usage', note: 'feeds the KI-Nutzung page' },
      { label: 'OpenAI — API keys', href: 'https://platform.openai.com/settings/organization/api-keys' },
      { label: 'OpenAI — projects', href: 'https://platform.openai.com/settings/organization/projects' },
      { label: 'ElevenLabs — usage', href: 'https://elevenlabs.io/app/usage', note: 'TTS + sound effects' },
      { label: 'ElevenLabs — voice library', href: 'https://elevenlabs.io/app/voice-library' },
      { label: 'Google Cloud — Text-to-Speech API', href: 'https://console.cloud.google.com/apis/library/texttospeech.googleapis.com', note: 'enable + quota' },
      { label: 'Azure — Speech resources', href: 'https://portal.azure.com/#view/HubsExtension/BrowseResource/resourceType/Microsoft.CognitiveServices%2Faccounts', note: 'key + region' },
    ],
  },
  {
    title: 'Integrations',
    links: [
      { label: 'Google Cloud — OAuth credentials', href: 'https://console.cloud.google.com/apis/credentials', note: 'Google sign-in' },
    ],
  },
  {
    title: 'Code',
    links: [
      { label: 'GitHub — repository', href: GITHUB_REPO },
      { label: 'GitHub — pull requests', href: `${GITHUB_REPO}/pulls` },
      { label: 'GitHub — Actions', href: `${GITHUB_REPO}/actions`, note: 'backend + frontend test workflows' },
      { label: 'GitHub — repo secrets', href: `${GITHUB_REPO}/settings/secrets/actions` },
    ],
  },
  {
    title: 'Live app',
    links: [
      { label: 'Frontend — de.rodion.ch', href: 'https://de.rodion.ch' },
      { label: 'Backend — OpenAPI docs', href: `${apiOrigin()}/docs`, note: 'this build’s API host' },
    ],
  },
];

const ServiceLinksTab = () => (
  <div className="space-y-4 text-xs text-[var(--ink-700)]">
    <p className="text-[var(--ink-500)]">
      Consoles for the services this project runs on. All of them require a login.
    </p>

    <div className="grid gap-3 sm:grid-cols-2">
      {LINK_GROUPS.map((group) => (
        <div key={group.title} className="border border-[var(--rule)] bg-[var(--cream)]">
          <div className="border-b border-[var(--rule)] px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-[var(--ink-500)]">
            {group.title}
          </div>
          <div className="divide-y divide-[var(--rule)]">
            {group.links.map((link) => (
              <a
                key={link.href}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-baseline gap-2 px-3 py-1.5 hover:bg-white transition-colors"
              >
                <span className="font-medium text-[var(--ink-900)]">{link.label}</span>
                {link.note && (
                  <span className="text-[10px] text-[var(--ink-400)] truncate">{link.note}</span>
                )}
                <span className="ml-auto shrink-0 text-[10px] text-[var(--ink-400)] font-mono">↗</span>
              </a>
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

export default ServiceLinksTab;
